import { t } from 'i18next';

import { ContextMenuItem } from '../../../../../../../react/components/skyview/context-menu';
import { DesignFeature } from '../../../../../../../react/components/skyview/right-menu/tools/design-tool-menu/sections/design-feature';
import { RevertChange } from '../../../../../../elements/design/design-node/design-node';
import { LineSegmentNode } from '../../../../../../elements/design/design-node/utils/line-segment-node';
import { publish, SubscriptionTopic } from '../../../../../../messaging/pubsub';
import { DesignTool } from '../../../../../design-tool';
import { addAndCommitAction } from '../../utils/actions';
import { divideLine } from '../../utils/curve';
import { deselect } from '../../utils/select';

function activateGenerateLineSegmentsFeature(tool: DesignTool): ContextMenuItem {
  return {
    title: t('contextMenu.functions.items.segmentLines', { ns: 'skyviewDesign' }),
    action: () => {
      tool.activeFeature = DesignFeature.GenerateLineSegments;
      publish(SubscriptionTopic.DesignToolMenuStateChanged, {
        designMode: false,
        snapMode: false,
        placeMode: false,
        features: true,
      });
      publish(SubscriptionTopic.DesignToolMultiselectUpdated, undefined);
      tool.contextMenuCallback?.();
    },
    hotkey: 'L',
  };
}

function applyGenerateLineSegmentsResult(
  tool: DesignTool,
  lineSegments: LineSegmentNode[],
  lineSegmentLength: number,
) {
  let changes: RevertChange[] = [];

  addAndCommitAction(tool, {
    commit: () => {
      changes = [];
      lineSegments.forEach((node) =>
        changes.unshift(tool.asset.designNode.removeLineSegment(node.id)),
      );

      for (const line of lineSegments) {
        const node = tool.asset.designNode;
        const start = node.getVertexByPointId(line.values[0], 'world');
        const end = node.getVertexByPointId(line.values[1], 'world');
        // Curved lines are divided along the curve, and the pieces follow the curve.
        const { points, curves, distances } = divideLine(start, end, line.curve, lineSegmentLength);

        const pointIds = [line.values[0]];

        for (const point of points) {
          const result = node.addPoint(node.getLocalPosition(point), line.attributes);
          changes.unshift(result);
          pointIds.push(result.id);
        }

        pointIds.push(line.values[1]);

        curves.forEach((curve, i) => {
          changes.unshift(
            node.addLineSegment(pointIds[i], pointIds[i + 1], line.attributes, curve, {
              width: line.width,
              stripe: line.stripe && {
                ...line.stripe,
                offset: line.stripe.offset + distances[i],
              },
            }),
          );
        });
      }

      tool.asset.designNode.refresh();
      tool.asset.refreshMetadata();
    },
    rollback: () => {
      tool.asset.designNode.revert(changes);
      tool.asset.designNode.refresh();
      tool.asset.refreshMetadata();
    },
  });

  deselect(tool);
}

export const lineSegmentFunctions = {
  activateGenerateLineSegmentsFeature,
  applyGenerateLineSegmentsResult,
};
